<script setup lang="ts">
    import { collapsibleKey } from './context';

    const props = withDefaults(
        defineProps<{
            /** Controlled state, for `v-model:open`. Leave unset to let the component keep its own state */
            open?: boolean;
            /** Initial state when `open` is unset */
            defaultOpen?: boolean;
            disabled?: boolean;
        }>(),
        // Without this default Vue casts a missing `open` to `false`, and the component could never be uncontrolled
        { open: undefined },
    );

    const emit = defineEmits<{
        'update:open': [open: boolean];
    }>();

    // Used only while `open` is unset; a set `open` always wins
    const localOpen = ref(props.defaultOpen);
    const isOpen = computed(() => props.open ?? localOpen.value);
    const disabled = computed(() => props.disabled);
    const contentId = useId();

    const toggle = () => {
        if (disabled.value) return;
        localOpen.value = !isOpen.value;
        emit('update:open', localOpen.value);
    };

    provide(collapsibleKey, { open: isOpen, disabled, contentId, toggle });

    /** For a trigger that must be another component (a Button, a menu item): bind these onto it */
    const triggerAttrs = computed(() => ({
        'aria-expanded': isOpen.value,
        'aria-controls': contentId,
        'data-state': isOpen.value ? 'open' : 'closed',
        disabled: disabled.value || undefined,
        onClick: toggle,
    }));
</script>

<template>
    <div
        class="ui-collapsible"
        :data-state="isOpen ? 'open' : 'closed'"
        :data-disabled="disabled || undefined"
    >
        <slot
            :open="isOpen"
            :toggle="toggle"
            :trigger-attrs="triggerAttrs"
        />
    </div>
</template>
